import classNames from 'classnames';
import {type FC, useState} from 'react';
import {MdOutlineClose, MdUnfoldMore} from 'react-icons/md';
import {
  type HashtagCategoryType,
  hashtagCategories,
} from '../../../../../core/entities/entrysheetBuilder/constants';
import type {GetHashtagType} from '../../../../../core/entities/register/schema';
import {EditButton} from './EditButton';

type Props = {
  index: number;
  handleSelectHashtag: (index?: number) => void;
  cancel: (id: number) => void;
  setSelectedHashtags: (hashtags: GetHashtagType[]) => void;
  selectedOptions: string[];
  selectedHashtags: GetHashtagType[];
  setHashtagCategory: (category: HashtagCategoryType) => void;
  updateEditingState: (index: number, isEditing: boolean) => void;
};

export const HashtagSelect: FC<Props> = ({
  index,
  setSelectedHashtags,
  selectedOptions,
  selectedHashtags,
  setHashtagCategory,
  handleSelectHashtag,
  cancel,
  updateEditingState,
}) => {
  const [editing, setEditing] = useState(false);
  // 現在のステップかどうか
  const isCurrentStep = index === selectedOptions.length;
  // 編集中もしくは現在のステップかどうか
  const isClickable = isCurrentStep || editing;

  return (
    <div>
      <div className="flex flex-wrap justify-end gap-8 max-w-3/4 pc:max-w-1/2">
        {!isCurrentStep && (
          <EditButton
            editing={editing}
            editClick={() => {
              setEditing(!editing);
              updateEditingState(index, !editing);
            }}
          />
        )}
        {hashtagCategories?.map((option) => (
          <HashtagCategoryButton
            key={option.key ?? 'other'}
            name={option.name}
            disabled={!isClickable}
            onClick={() => {
              if (option.key === undefined) {
                const newSeelectedHashtags = selectedHashtags.filter(
                  (hashtag) => hashtag.id !== 0,
                );
                const otherHashtag = {
                  id: 0,
                  name: 'その他',
                  usedCount: 0,
                };
                setSelectedHashtags([...newSeelectedHashtags, otherHashtag]);
              } else {
                setHashtagCategory(option);
              }
            }}
          />
        ))}
      </div>
      <div className="mt-16 flex flex-wrap justify-end gap-8 max-w-3/4 pc:max-w-1/2">
        {selectedHashtags.map((hashtag) => (
          <Hashtag
            key={hashtag.id}
            name={hashtag.name}
            id={hashtag.id}
            onCancel={() => {
              cancel(hashtag.id);
            }}
            deletable={isClickable}
          />
        ))}
      </div>
      {selectedHashtags.length > 0 && isClickable ? (
        <div className="mt-16 flex flex-wrap justify-end gap-8 max-w-3/4 pc:max-w-1/2">
          <div className="w-[92px] h-40 bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r rounded-full p-[2px] cursor-pointer hover:opacity-60">
            <button
              type="button"
              className="gap-8 rounded-full px-16 py-8 text-base text-white"
              onClick={() => {
                if (editing) {
                  handleSelectHashtag(index);
                } else {
                  handleSelectHashtag();
                }

                setEditing(false);
                updateEditingState(index, false);
              }}
            >
              {editing ? '更新する' : '次に進む'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

type HashtagCategoryProps = {
  name: string;
  disabled: boolean;
  onClick: () => void;
};

const HashtagCategoryButton: FC<HashtagCategoryProps> = ({
  name,
  disabled,
  onClick,
}) => {
  return (
    <button
      type="button"
      className={classNames(
        'bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r rounded-full p-[2px]',
        'enabled:cursor-pointer enabled:hover:opacity-60',
        'disabled:cursor-not-allowed',
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <div className="rounded-full px-4 py-8 bg-white">
        <div className="flex items-center text-base bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r text-transparent bg-clip-text px-8">
          {name}
          {name !== 'その他' && (
            <MdUnfoldMore size={16} className="text-gradient-ai-l" />
          )}
        </div>
      </div>
    </button>
  );
};

type HashtagProps = {
  name: string;
  id: number;
  deletable: boolean;
  onCancel: () => void;
};

const Hashtag: FC<HashtagProps> = ({name, id, deletable, onCancel}) => {
  return (
    <div
      key={id}
      className="h-40 bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r rounded-full p-[2px]"
    >
      <div className="flex items-center gap-8 rounded-full px-16 py-8">
        <div className="text-base text-white">{name}</div>
        <button
          type="button"
          onClick={onCancel}
          disabled={!deletable}
          className="cursor-pointer disabled:cursor-not-allowed"
        >
          <MdOutlineClose size={14} color="white" />
        </button>
      </div>
    </div>
  );
};
